import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Input } from "antd";
import ConfirmationModal from "../../../../ers/common/components/ConfirmationModal";
import CommonSelect from "../../../../../core/common/commonSelect";
import { TemplateStructure } from "./TemplateDetailsModal";

const { TextArea } = Input;

// Types
type Mode = "add" | "edit";

export interface TemplateRecord {
  id?: string | number;
  name: string;
  category?: string;
  language?: string;
  campaign_name?: string | null;
  structure?: TemplateStructure;
  status?: string;
  is_manual_entry?: boolean;
  is_active?: number;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateData {
  id: number | string;
  name: string;
  category?: string;
  language?: string;
  status: string;
  is_active?: number;
  is_manual_entry?: boolean;
  template_hash?: string;
  last_synced_at?: string;
}

interface AddTemplateModalProps {
  isOpen: boolean;
  mode?: Mode;
  templateRecord?: TemplateRecord | null;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void> | void;
  configId: string;
  companyId: number;
  companyType: string;
}

type FieldErrors = {
  name?: string;
  templateBody?: string;
};

const OPTIONS = {
  category: [
    { value: "MARKETING", label: "Marketing" },
    { value: "UTILITY", label: "Utility" },
    { value: "AUTHENTICATION", label: "Authentication" },
  ],
  language: [
    { value: "en", label: "English" },
    { value: "en_US", label: "English (US)" },
    { value: "en_GB", label: "English (GB)" },
    { value: "hi", label: "Hindi" },
  ],
  status: [
    { value: "APPROVED", label: "Approved" },
    { value: "PENDING", label: "Pending" },
    { value: "REJECTED", label: "Rejected" },
  ],
};

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({
  isOpen,
  mode = "add",
  templateRecord,
  onClose,
  onSubmit,
  configId,
  companyId,
  companyType,
}) => {
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [category, setCategory] = useState("MARKETING");
  const [campaignName, setCampaignName] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState("APPROVED");

  const modalRef = useRef<HTMLDivElement | null>(null);
  const mouseDownInside = useRef(false);

  const resetForm = useCallback(() => {
    setName("");
    setLanguage("en");
    setCategory("MARKETING");
    setCampaignName("");
    setTemplateBody("");
    setStatus("APPROVED");
    setErrors({});
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setIsSubmitting(false);
      setIsConfirmOpen(false);
      setIsConfirmLoading(false);
      return;
    }

    if (mode === "edit" && templateRecord) {
      setName(templateRecord.name || "");
      setLanguage(templateRecord.language || "en");
      setCategory(templateRecord.category || "MARKETING");
      setCampaignName(templateRecord.campaign_name || "");
      setStatus(templateRecord?.status || "APPROVED")

      // Extract template body from structure
      const bodyComponent = templateRecord.structure?.components?.find(
        (c) => c.type === "BODY"
      );
      setTemplateBody(bodyComponent?.text || "");
    } else {
      resetForm();
    }
  }, [isOpen, mode, templateRecord, resetForm]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        e.preventDefault();
        if (isConfirmOpen) {
          setIsConfirmOpen(false);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKey);
    }
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isSubmitting, isConfirmOpen, onClose]);

  useEffect(() => {
    if (isOpen || isConfirmOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isConfirmOpen]);

  const clearError = useCallback((field: keyof FieldErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validate = (): boolean => {
    const newErrors: FieldErrors = {};
    if (!name.trim()) newErrors.name = "Enter template name";
    if (!templateBody.trim()) newErrors.templateBody = "Paste template body";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = useCallback(() => {
    const varMatches = templateBody.match(/\{\{(\d+)\}\}/g) || [];
    const varNumbers = varMatches.map(match => parseInt(match.replace(/\{\{|\}\}/g, '')));
    const uniqueVarNumbers = Array.from(new Set(varNumbers)).sort((a, b) => a - b);

    const variables = uniqueVarNumbers.length > 0
      ? uniqueVarNumbers.reduce((acc, num) => {
        acc[`var${num}`] = num;
        return acc;
      }, {} as Record<string, number>)
      : undefined;

    const payload: any = {
      signature: localStorage.getItem("signature"),
      config_id: Number(configId),
      company_id: companyId,
      company_type: companyType,
      name: name.trim().toLowerCase(),
      language,
      category,
      campaign_name: campaignName.trim() || null,
      status: status,
      structure: {
        variables: variables,
        components: [
          {
            type: "BODY",
            text: templateBody.trim(),
            example: { body_text: "Sample {{1}} {{2}}" },
          },
        ],
      },
    };

    if (mode === "edit" && templateRecord?.id) {
      payload.id = templateRecord.id;
    }

    return payload;
  }, [
    mode,
    templateRecord,
    configId,
    companyId,
    companyType,
    name,
    language,
    category,
    campaignName,
    templateBody,
    status
  ]);


  const handleSubmit = useCallback(async () => {
    setIsConfirmLoading(true);
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      await onSubmit(payload);
      setIsConfirmOpen(false);
      onClose();
    } catch (error: any) {
      console.error("Submit error:", error);
      const msg =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        "Failed to save template. Please try again.";
      setIsConfirmOpen(false);
      setErrors((prev) => ({ ...prev, name: msg }));
    } finally {
      setIsConfirmLoading(false);
      setIsSubmitting(false);
    }
  }, [onSubmit, onClose, buildPayload]);

  if (!isOpen) return null;

  const varCount = (templateBody.match(/\{\{(\d+)\}\}/g) || []).length;

  // Dynamic text based on mode
  const modalTitle = mode === "edit" ? "Edit Template" : "Add Manual Template";
  const modalSubtitle =
    mode === "edit"
      ? "Update template details"
      : "Copy template from Fonada/WhatsApp";
  const submitButtonText =
    mode === "edit" ? "Update Template" : "Create Template";
  const confirmTitle = mode === "edit" ? "Update Template" : "Create Template";
  const confirmMessage =
    mode === "edit"
      ? `Template "${name}" will be updated with ${varCount} variables. This may affect existing workflows.`
      : `Template "${name}" will be created with ${varCount} variables.`;
  const confirmType = mode === "edit" ? "warning" : "info";
  const confirmButtonText = mode === "edit" ? "Yes, Update" : "Yes, Create";

  return createPortal(
    <>
      <div
        className="modal-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          padding: 24,
          boxSizing: "border-box",
        }}
        onMouseDown={(e) => {
          if (modalRef.current?.contains(e.target as Node)) {
            mouseDownInside.current = true;
          } else {
            mouseDownInside.current = false;
          }
        }}
        onMouseUp={(e) => {
          const clickedOutside =
            !mouseDownInside.current &&
            modalRef.current &&
            !modalRef.current.contains(e.target as Node);
          mouseDownInside.current = false;
          if (clickedOutside && !isSubmitting) onClose();
        }}>
        <div
          ref={modalRef}
          className="shadow-lg"
          style={{
            background: "#ffffff",
            borderRadius: 18,
            width: "100%",
            maxWidth: 800,
            height: "80vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow:
              "0 28px 60px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148,163,184,0.18)",
          }}>
          <div
            className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
            style={{ background: "#f5f7fb" }}>
            <div>
              <h5 className="mb-0 fw-semibold">{modalTitle}</h5>
              <small className="text-muted">{modalSubtitle}</small>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 22,
                lineHeight: 1,
                cursor: "pointer",
                color: "#6b7280",
              }}>
              ×
            </button>
          </div>

          <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
            <div
              className="mb-4"
              style={{ borderBottom: "1px solid #edf0f3", paddingBottom: 12 }}>
              <span
                className="text-muted small"
                style={{ letterSpacing: "0.05em" }}>
                TEMPLATE INFO
              </span>
            </div>

            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="form-label fw-semibold mb-1 small">
                  Template Name <span className="text-danger">*</span>
                </label>
                <Input
                  placeholder="e.g. class_roll_reminder"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  className={errors.name ? "mb-1" : "mb-2"}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name}</small>
                )}
                {!errors.name && (
                  <small className="text-muted">snake_case format</small>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}>
                <div>
                  <label className="form-label fw-semibold mb-1 small">
                    Language
                  </label>
                  <CommonSelect
                    className="w-100"
                    options={OPTIONS.language}
                    defaultValue={{
                      value: language,
                      label:
                        OPTIONS.language.find((o) => o.value === language)
                          ?.label || "Select Language",
                    }}
                    placeHolder="en"
                    onChange={(selected: any) =>
                      setLanguage(selected?.value || "en")
                    }
                  />
                </div>
                <div>
                  <label className="form-label fw-semibold mb-1 small">
                    Category
                  </label>
                  <CommonSelect
                    className="w-100"
                    options={OPTIONS.category}
                    defaultValue={{
                      value: category,
                      label:
                        OPTIONS.category.find((o) => o.value === category)
                          ?.label || "Select Category",
                    }}
                    placeHolder="MARKETING"
                    onChange={(selected: any) =>
                      setCategory(selected?.value || "MARKETING")
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}>
                <div>
                  <label className="form-label fw-semibold mb-1 small">
                    Campaign Name
                  </label>
                  <Input
                    className="select"
                    placeholder="e.g. agriculture_dec_2025"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                  <small className="text-info">Mandatory for fonada</small>
                </div>
                <div>
                  <label className="form-label fw-semibold mb-1 small">
                    Template Status
                  </label>
                  <CommonSelect
                    className="w-100"
                    options={OPTIONS.status}
                    defaultValue={{
                      value: status,
                      label:
                        OPTIONS.status.find((o) => o.value === status)
                          ?.label || "Select Status",
                    }}
                    placeHolder="APPROVED"
                    onChange={(selected: any) => {
                      setStatus(selected?.value)
                    }
                    }
                  />
                </div>
              </div>

              <div>
                <label className="form-label fw-semibold mb-1 small">
                  Template Body <span className="text-danger">*</span>
                </label>
                <TextArea
                  rows={8}
                  placeholder="Paste your template body here:
Hello {{1}}, your class roll is {{2}}. Join: {{3}}"
                  value={templateBody}
                  onChange={(e) => {
                    setTemplateBody(e.target.value);
                    clearError("templateBody");
                  }}
                  className={errors.templateBody ? "" : "mb-2"}
                  style={{ fontFamily: "monospace" }}
                />
                {errors.templateBody && (
                  <small className="text-danger">{errors.templateBody}</small>
                )}
                <small className="text-muted">
                  Variables detected: <strong>{varCount}</strong>
                </small>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
            <small className="text-muted">
              Variables: {`{{1}}, {{2}}, {{3}}`}...
            </small>
            <div className="d-flex" style={{ gap: 10 }}>
              <button
                type="button"
                className="btn btn-light"
                onClick={onClose}
                disabled={isSubmitting}>
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${mode === "edit" ? "btn-warning" : "btn-primary"
                  }`}
                onClick={() => validate() && setIsConfirmOpen(true)}
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {mode === "edit" ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  submitButtonText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleSubmit}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={confirmButtonText}
        cancelText="Cancel"
        type={confirmType}
        isLoading={isConfirmLoading}
        showCloseButton={false}
      />
    </>,
    document.body
  );
};

export default AddTemplateModal;
